// src/components/Login.js
import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import { signInWithEmailAndPassword, sendPasswordResetEmail, getMultiFactorResolver, signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../apiConfig';
import { ACCOUNT_DISABLED_MESSAGE } from '../utils/accountMessages';
import { trackEvent } from '../services/analyticsService';
import { SIGNED_OUT_REASON_KEY, signedOutMessage } from '../utils/sessionLimits';
import MfaChallengeDialog from './MfaChallengeDialog';
import './Login.css';

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const microsoftBtnStyle = {
  backgroundColor: '#0078d4',
  marginTop: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

function Login() {
  // Why the last session ended, left by AuthContext. Read once and removed so
  // it shows only on the first visit after the sign-out.
  const [signedOutNotice] = useState(() => {
    try {
      const reason = sessionStorage.getItem(SIGNED_OUT_REASON_KEY);
      if (reason) sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
      return signedOutMessage(reason);
    } catch (error) {
      return null;
    }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMfaChallenge, setShowMfaChallenge] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [ssoInfo, setSsoInfo] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showPasswordFallback, setShowPasswordFallback] = useState(false);
  const navigate = useNavigate();
  const { resumePostLogin, setPostLoginInProgress } = useAuth();

  // Every sign-in *on this screen* funnels through here — password,
  // password+MFA and SSO alike — so this is the one place `login` can be
  // counted exactly once. It is not every sign-in in the app: StartTrialForm
  // signs a brand-new user in itself and lands on /tasks with a hard
  // navigation, never touching this component. That is a `sign_up`, not a
  // `login`, and it is reported there.
  //
  // Fired before resumePostLogin because authentication has already succeeded:
  // the routing that follows can still end at /force-password-reset, /enroll-mfa
  // or a signed-out "no workspace" error, and none of those un-happen the login.
  //
  // Options object rather than a positional `method`, so a call site that ever
  // becomes `onClick={handlePostLogin}` degrades to the default instead of
  // reporting a React synthetic event as the login method.
  const handlePostLogin = async ({ method = 'password' } = {}) => {
    trackEvent('login', { method });
    await resumePostLogin(navigate);
  };

  const handleMfaSuccess = async () => {
    setShowMfaChallenge(false);
    // resolveSignIn() inside MfaChallengeDialog has already flipped currentUser
    // by the time this runs — re-arm the flag so the /login route guard stays
    // suppressed while resumePostLogin picks the destination.
    setPostLoginInProgress(true);
    try {
      await handlePostLogin();
    } catch (err) {
      setPostLoginInProgress(false);
      setError(err.message || 'An error occurred after MFA verification.');
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (ssoInfo?.sso_enforce) return;
    setError(null);
    setLoading(true);
    // Suppress the /login route guard for the entire sign-in window. Without
    // this, onAuthStateChanged sets currentUser before resumePostLogin gets
    // its first synchronous chance to flip the flag, and the route briefly
    // redirects to /select-subscriber for a single-subscriber user.
    setPostLoginInProgress(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      await handlePostLogin();
    } catch (err) {
      setPostLoginInProgress(false);
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);
        setMfaResolver(resolver);
        setShowMfaChallenge(true);
        setLoading(false);
        return;
      }

      let friendlyMsg = 'An error occurred. Please try again.';
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        friendlyMsg = 'Incorrect email or password.';
      } else if (err.code === 'auth/invalid-email') {
        friendlyMsg = 'Please enter a valid email address.';
      } else if (err.code === 'auth/too-many-requests') {
        friendlyMsg = 'Too many failed attempts. Please try again later or reset your password.';
      } else if (err.code === 'auth/user-disabled') {
        // Set by the workspace purge on an account left with no access at all.
        // Without this branch Firebase's raw 'Firebase: Error
        // (auth/user-disabled).' reaches the user, which reads like a fault.
        friendlyMsg = ACCOUNT_DISABLED_MESSAGE;
      } else if (err.message) {
        friendlyMsg = err.message;
      }
      setError(friendlyMsg);
      setLoading(false);
      console.error("Login error:", err);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage(null);
    setError(null);
    if (!email) {
      setError('Please enter your email address to reset your password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMessage('Password reset email sent! Please check your inbox.');
    } catch (err) {
      setError(err.message);
    }
  };

  const checkEmailDomain = async () => {
    const domain = email.split('@')[1];
    if (!domain || domain.length < 3) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/auth/sso/lookup?email=${encodeURIComponent(email)}`);
      if (resp.ok) {
        const data = await resp.json();
        setSsoInfo(data.sso_enabled ? data : null);
        // Password input unmounts on the SSO branch — drop whatever was typed
        // for the previous email so it can't be submitted against a new one.
        if (data.sso_enabled) setPassword('');
      }
    } catch (e) {
      // SSO lookup failure is non-fatal — fall through to password
    }
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    setError(null);
    setPostLoginInProgress(true);
    try {
      const domain = email.split('@')[1];
      const resp = await fetch(`${API_BASE_URL}/api/v1/auth/sso/auth-url?domain=${encodeURIComponent(domain)}`);
      if (!resp.ok) throw new Error('Failed to get SSO URL');
      const { auth_url } = await resp.json();

      const popup = window.open(auth_url, 'azure-sso', 'width=500,height=700,scrollbars=yes');
      if (!popup) {
        setError('Popup was blocked. Please allow popups for this site.');
        setSsoLoading(false);
        setPostLoginInProgress(false);
        return;
      }

      const expectedOrigin = new URL(API_BASE_URL).origin;
      let pollId = null;
      const cleanup = () => {
        if (pollId) clearInterval(pollId);
        window.removeEventListener('message', handler);
      };
      const handler = async (event) => {
        if (event.origin !== expectedOrigin) return;
        if (event.data?.type !== 'azure-sso') return;
        cleanup();
        if (!popup.closed) popup.close();

        if (event.data.status === 'success' && event.data.custom_token) {
          try {
            await signInWithCustomToken(auth, event.data.custom_token);
            await handlePostLogin({ method: 'sso' });
          } catch (err) {
            setError('SSO authentication failed. Please try again.');
            setSsoLoading(false);
            setPostLoginInProgress(false);
          }
        } else {
          setError(event.data.error || 'SSO login failed');
          setSsoLoading(false);
          setPostLoginInProgress(false);
        }
      };
      window.addEventListener('message', handler);

      // Clean up listener if popup is closed without completing SSO
      pollId = setInterval(() => {
        if (popup.closed) {
          cleanup();
          setSsoLoading(false);
          setPostLoginInProgress(false);
        }
      }, 1000);
    } catch (e) {
      setError('Failed to initiate SSO login');
      setPostLoginInProgress(false);
      setSsoLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="login-title">Login</h2>
        {signedOutNotice && (
          <Alert severity="info" sx={{ mb: 2 }}>{signedOutNotice}</Alert>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setSsoInfo(null); setShowPasswordFallback(false); }}
              onBlur={checkEmailDomain}
              required
              className="login-input"
              autoComplete="email"
              autoFocus
            />
          </div>

          {/* SSO enforce mode: only show Microsoft button */}
          {ssoInfo?.sso_enforce && (
            <>
              {error && <div className="login-error">{error}</div>}
              {resetMessage && <div className="login-reset-message">{resetMessage}</div>}
              <button
                type="button"
                className="login-btn"
                onClick={handleSsoLogin}
                disabled={ssoLoading}
                style={microsoftBtnStyle}
              >
                {ssoLoading ? <span className="login-spinner"></span> : (
                  <><MicrosoftIcon /> Sign in with Microsoft</>
                )}
              </button>
            </>
          )}

          {/* SSO detected but not enforced: show Microsoft button + optional password fallback */}
          {ssoInfo && !ssoInfo.sso_enforce && (
            <>
              {error && <div className="login-error">{error}</div>}
              {resetMessage && <div className="login-reset-message">{resetMessage}</div>}
              <button
                type="button"
                className="login-btn"
                onClick={handleSsoLogin}
                disabled={ssoLoading}
                style={microsoftBtnStyle}
              >
                {ssoLoading ? <span className="login-spinner"></span> : (
                  <><MicrosoftIcon /> Sign in with Microsoft</>
                )}
              </button>
              {!showPasswordFallback ? (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowPasswordFallback(true)}
                    style={{ background: 'none', border: 'none', color: '#137a3e', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline' }}
                  >
                    Or use email and password
                  </button>
                </div>
              ) : (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label htmlFor="login-password">Password</label>
                    <input
                      id="login-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="login-input"
                      autoComplete="current-password"
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <button className="forgot-password-link" style={{ background: 'none', border: 'none', color: '#137a3e', cursor: 'pointer', padding: 0, fontSize: '0.98rem' }} onClick={handleForgotPassword} type="button">
                      Forgot password?
                    </button>
                  </div>
                  <button type="submit" className="login-btn" disabled={loading}>
                    {loading ? <span className="login-spinner"></span> : 'Login'}
                  </button>
                </>
              )}
            </>
          )}

          {/* No SSO: normal password login */}
          {!ssoInfo && (
            <>
              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="login-input"
                  autoComplete="current-password"
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button className="forgot-password-link" style={{ background: 'none', border: 'none', color: '#137a3e', cursor: 'pointer', padding: 0, fontSize: '0.98rem' }} onClick={handleForgotPassword} type="button">
                  Forgot password?
                </button>
              </div>
              {error && <div className="login-error">{error}</div>}
              {resetMessage && <div className="login-reset-message">{resetMessage}</div>}
              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? <span className="login-spinner"></span> : 'Login'}
              </button>
            </>
          )}
        </form>
      </div>

      <MfaChallengeDialog
        open={showMfaChallenge}
        resolver={mfaResolver}
        onSuccess={handleMfaSuccess}
        onCancel={() => { setShowMfaChallenge(false); setLoading(false); }}
      />
    </div>
  );
}
export default Login;
